import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IndexedData } from '../interfaces/indexed-data';
import { IndexedDbName } from '../enums/indexed-db-name';
import { indexedDbNameToken } from '../tokens/indexed-db-token';

@Injectable({
    providedIn: 'root'
})
export class IndexedDbService {
    protected readonly indexedDbName = inject<IndexedDbName>(indexedDbNameToken);


    private static readonly RECORD_EXPIRATION_MS = 24 * 60 * 60 * 1000;

    private idbDatabase!: IDBDatabase;

    constructor() {
        const indexedDbName = this.indexedDbName;

        this.initialize(indexedDbName);
    }

    addDataIfNotExists<T extends { id?: number }>(storeName: string, data: T): Observable<number> {
        return new Observable<number>((observer) => {
            this.createStoreIfNotExists(storeName).subscribe({
                next: () => {
                    const indexedData: IndexedData<T> = {
                        ...(data.id !== undefined && { id: data.id }),
                        data,
                        timestamp: Date.now()
                    };
                    const transaction = this.idbDatabase.transaction(storeName, 'readwrite');
                    const store = transaction.objectStore(storeName);
                    const id = (data as T).id;
                    if (id !== undefined && id !== null) {
                        const getRequest = store.get(id);
                        getRequest.onsuccess = () => {
                            if (!getRequest.result) {
                                const addRequest = store.add(indexedData);
                                addRequest.onsuccess = () => {
                                    observer.next(addRequest.result as number);
                                    observer.complete();
                                };
                                addRequest.onerror = () => observer.error(addRequest.error);
                            }
                        };
                        getRequest.onerror = () => observer.error(getRequest.error);
                    } else {
                        const addRequest = store.add(indexedData);
                        addRequest.onsuccess = () => {
                            observer.next(addRequest.result as number);
                            observer.complete();
                        };
                        addRequest.onerror = () => observer.error(addRequest.error);
                    }
                },
                error: err => observer.error(err)
            });
        });
    }

    getAllData<T>(storeName: string): Observable<T[]> {
        return new Observable<T[]>((observer) => {
            this.createStoreIfNotExists(storeName).subscribe({
                next: () => {
                    const transaction = this.idbDatabase.transaction(storeName, 'readonly');
                    const store = transaction.objectStore(storeName);
                    const request = store.getAll();
                    request.onsuccess = () => {
                        observer.next(request.result.map((res: IndexedData<T>) => res.data));
                        observer.complete();
                    };
                    request.onerror = () => observer.error(request.error);
                },
                error: err => observer.error(err)
            });
        });
    }

    deleteData(storeName: string, id: number): Observable<void> {
        return new Observable<void>((observer) => {
            this.createStoreIfNotExists(storeName).subscribe({
                next: () => {
                    const transaction = this.idbDatabase.transaction(storeName, 'readwrite');
                    const store = transaction.objectStore(storeName);
                    const request = store.delete(id);
                    request.onsuccess = () => {
                        observer.next();
                        observer.complete();
                    };
                    request.onerror = () => observer.error(request.error);
                },
                error: err => observer.error(err)
            });
        });
    }

    purgeExpiredData(): Observable<void> {
        return new Observable<void>((observer) => {
            const storeNames = Array.from(this.idbDatabase.objectStoreNames);
            if (storeNames.length === 0) {
                observer.next();
                observer.complete();
                return;
            }
            const transaction = this.idbDatabase.transaction(storeNames, 'readwrite');
            let pendingStores = storeNames.length;
            storeNames.forEach(storeName => {
                const store = transaction.objectStore(storeName);
                const request = store.getAll();
                request.onsuccess = () => {
                    const now = Date.now();
                    const oldItems = request.result.filter((item: { timestamp: number }) =>
                        now - item.timestamp > IndexedDbService.RECORD_EXPIRATION_MS
                    );
                    oldItems.forEach((item: { id?: number }) => {
                        if (item.id !== undefined) {
                            store.delete(item.id);
                        }
                    });
                    pendingStores--;
                    if (pendingStores === 0) {
                        observer.next();
                        observer.complete();
                    }
                };
                request.onerror = () => observer.error(request.error);
            });
        });
    }

    private initialize(indexedDbName: IndexedDbName) {
        if (!indexedDbName) {
            throw new Error('Indexed database name is required.');
        }
        const openDBRequest = indexedDB.open(indexedDbName);
        openDBRequest.onsuccess = (event: Event) => {
            const target = event.target as IDBOpenDBRequest | undefined;
            if (target && target.result) {
                this.idbDatabase = target.result as IDBDatabase;
                this.purgeExpiredData();
            }
        };
        openDBRequest.onerror = (event: Event) => {
            const target = event.target as IDBOpenDBRequest | null;
            const error = target?.error;
            console.error('Failed to initialize indexed database.', error);
        };
    }

    private createStoreIfNotExists(storeName: string): Observable<void> {
        return new Observable<void>((observer) => {
            if (this.idbDatabase.objectStoreNames.contains(storeName)) {
                observer.next();
                observer.complete();
                return;
            }
            const newVersion = this.idbDatabase.version + 1;
            this.idbDatabase.close();
            const openDBRequest = indexedDB.open(this.indexedDbName, newVersion);
            openDBRequest.onupgradeneeded = (event: Event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(storeName)) {
                    db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
                }
            };
            openDBRequest.onsuccess = (event: Event) => {
                this.idbDatabase = (event.target as IDBOpenDBRequest).result;
                observer.next();
                observer.complete();
            };
            openDBRequest.onerror = (event: Event) => {
                observer.error((event.target as IDBOpenDBRequest).error);
            };
        });
    }
}
